/**
 * File: assets/basari-insurance.js
 * Purpose: Insurance contact form validation, Shopify submit, and success modal.
 *
 * The form posts with Shopify's native contact form. novalidate is set on the
 * form so errors render in the existing field alerts instead of the browser
 * tooltip. This file is that validation.
 *
 * The success modal opens only when Liquid reports form.posted_successfully?.
 */
(function (global) {
  'use strict';

  var ROOT_SELECTOR = '[data-basari-insurance]';
  var READY_ATTR = 'data-basari-insurance-ready';
  var FORM_SELECTOR = 'form[data-basari-insurance-form], form.basari-insurance-form__form';
  var SUBMIT_SELECTOR = '[data-basari-insurance-submit]';
  var MODAL_SELECTOR = '[data-basari-success-modal]';
  var MODAL_CLOSE_SELECTOR = '[data-basari-success-modal-close]';
  var MODAL_BACKDROP_SELECTOR = '[data-basari-success-modal-backdrop]';
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var SUBMIT_LOCK_MS = 15000;

  var BasariInsurance = global.BasariInsurance || {};

  function emit(root, name, detail) {
    if (!root) return;
    root.dispatchEvent(
      new CustomEvent(name, {
        bubbles: true,
        detail: detail || {}
      })
    );
  }

  function qs(root, selector) {
    return root ? root.querySelector(selector) : null;
  }

  function qsa(root, selector) {
    return root ? Array.prototype.slice.call(root.querySelectorAll(selector)) : [];
  }

  function normalizeText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function message(root, name, fallback) {
    return root.getAttribute(name) || fallback;
  }

  function controlLabel(input) {
    if (input.id) {
      var explicit = document.querySelector('label[for="' + input.id.replace(/"/g, '\\"') + '"]');
      if (explicit && normalizeText(explicit.textContent)) return normalizeText(explicit.textContent);
    }

    var labelledBy = input.getAttribute('aria-labelledby');
    if (labelledBy) {
      var text = labelledBy.split(/\s+/).map(function (id) {
        var el = document.getElementById(id);
        return el ? el.textContent : '';
      }).join(' ');
      if (normalizeText(text)) return normalizeText(text);
    }

    var wrapper = input.closest('[data-basari-field]');
    if (wrapper) {
      var fallback = wrapper.querySelector('label, .basari-form-field__label, .basari-select-field__label');
      if (fallback && normalizeText(fallback.textContent)) return normalizeText(fallback.textContent);
    }

    return input.getAttribute('data-basari-field-key') || 'Field';
  }

  function fieldError(input) {
    var wrapper = input.closest('[data-basari-field]');
    return wrapper ? wrapper.querySelector('[data-basari-field-error]') : null;
  }

  function setFieldError(input, text) {
    var error = fieldError(input);
    input.setAttribute('aria-invalid', 'true');
    if (!error) return;
    error.textContent = text;
    error.removeAttribute('hidden');
    if (error.id) input.setAttribute('aria-describedby', error.id);
  }

  function clearFieldError(input) {
    input.removeAttribute('aria-invalid');
    var describedBy = input.getAttribute('aria-describedby');
    var error = fieldError(input);
    if (error && describedBy === error.id) input.removeAttribute('aria-describedby');
    if (!error) return;
    var wrapper = input.closest('[data-basari-field]');
    if (wrapper && wrapper.querySelector('[aria-invalid="true"]')) return;
    error.textContent = '';
    error.setAttribute('hidden', '');
  }

  function clearErrors(form) {
    qsa(form, '[data-basari-field-input]').forEach(clearFieldError);
  }

  function phoneDigits(value) {
    return String(value || '').replace(/\D/g, '');
  }

  function validateForm(root, form) {
    var requiredMessage = message(root, 'data-error-required', 'This field is required.');
    var emailMessage = message(root, 'data-error-email', 'Enter a valid email address.');
    var phoneMessage = message(root, 'data-error-phone', 'Enter a valid phone number.');
    var termsMessage = message(root, 'data-error-terms', 'Please accept the terms and conditions.');
    var invalid = [];

    qsa(form, '[data-basari-field-input]').forEach(function (input) {
      if (input.type === 'hidden' || input.type === 'file') return;
      var key = input.getAttribute('data-basari-field-key');
      var value = input.type === 'checkbox' ? input.checked : normalizeText(input.value);

      if (input.type === 'checkbox') {
        if (input.required && !input.checked) invalid.push({ input: input, message: termsMessage });
        return;
      }

      if (key === 'member_id') return;

      if (!value) {
        invalid.push({ input: input, message: requiredMessage });
        return;
      }

      if (key === 'email' && !EMAIL_PATTERN.test(value)) {
        invalid.push({ input: input, message: emailMessage });
        return;
      }

      if (key === 'phone') {
        var digits = phoneDigits(value);
        if (digits.length < 7 || digits.length > 15) {
          invalid.push({ input: input, message: phoneMessage });
        }
      }
    });

    return invalid;
  }

  var DRAFT_FIELD_KEYS = {
    country_code: true,
    insurance_provider: true,
    nationality: true,
    emirate: true
  };

  function collectFields(form) {
    var data = {};
    qsa(form, '[data-basari-field-input]').forEach(function (input) {
      var key = input.getAttribute('data-basari-field-key');
      if (!key || input.type === 'file') return;
      data[key] = input.type === 'checkbox' ? !!input.checked : input.value;
    });
    return data;
  }

  function isDraftField(key) {
    return !!DRAFT_FIELD_KEYS[key];
  }

  function collectDraft(form) {
    var draft = {};
    var fields = collectFields(form);
    Object.keys(fields).forEach(function (key) {
      if (isDraftField(key)) draft[key] = fields[key];
    });
    return draft;
  }

  function draftKey(root) {
    return 'basari-insurance-draft:' + (root.getAttribute('data-section-id') || 'insurance');
  }

  function readStoredDraft(root) {
    var key = draftKey(root);
    var raw = null;
    try {
      raw = sessionStorage.getItem(key);
    } catch (err) {
      return null;
    }
    if (!raw) return null;

    var data = null;
    try {
      data = JSON.parse(raw);
    } catch (err) {
      try {
        sessionStorage.removeItem(key);
      } catch (removeErr) {
        return null;
      }
      return null;
    }
    if (!data || typeof data !== 'object') {
      try {
        sessionStorage.removeItem(key);
      } catch (removeErr) {
        return null;
      }
      return null;
    }

    var safe = {};
    var changed = false;
    Object.keys(data).forEach(function (fieldKey) {
      if (isDraftField(fieldKey)) safe[fieldKey] = data[fieldKey];
      else changed = true;
    });

    if (changed) {
      try {
        if (Object.keys(safe).length) sessionStorage.setItem(key, JSON.stringify(safe));
        else sessionStorage.removeItem(key);
      } catch (err) {
        try {
          sessionStorage.removeItem(key);
        } catch (removeErr) {
          return null;
        }
      }
    }

    return Object.keys(safe).length ? safe : null;
  }

  function saveDraft(root, form) {
    var key = draftKey(root);
    try {
      var draft = collectDraft(form);
      if (!Object.keys(draft).length) {
        sessionStorage.removeItem(key);
        return;
      }
      sessionStorage.setItem(key, JSON.stringify(draft));
    } catch (err) {
      return;
    }
  }

  function clearDraft(root) {
    try {
      sessionStorage.removeItem(draftKey(root));
    } catch (err) {
      return;
    }
  }

  function restoreDraft(root, form) {
    var data = readStoredDraft(root);
    if (!data) return;

    Object.keys(data).forEach(function (key) {
      if (!isDraftField(key)) return;
      var input = form.querySelector('[data-basari-field-key="' + key + '"]');
      if (!input || input.type === 'hidden' || input.type === 'file' || input.type === 'checkbox') return;
      input.value = data[key] == null ? '' : String(data[key]);
    });
  }

  function writePayload(form) {
    qsa(form, '[data-basari-field-input]').forEach(function (input) {
      if (input.type === 'text' || input.type === 'email' || input.type === 'tel') {
        input.value = normalizeText(input.value);
      }
    });

    var codeInput = form.querySelector('[data-basari-field-key="country_code"]');
    var phoneInput = form.querySelector('[data-basari-field-key="phone"]');
    var code = codeInput ? normalizeText(codeInput.value) : '';
    var number = phoneInput ? normalizeText(phoneInput.value) : '';
    var phoneValue = form.querySelector('[data-basari-insurance-phone-value]');
    if (phoneValue) phoneValue.value = [code, number].filter(Boolean).join(' ');

    var body = form.querySelector('[data-basari-insurance-body]');
    if (!body) return;

    var lines = ['Insurance eligibility application', ''];
    qsa(form, '[data-basari-field-input]').forEach(function (input) {
      if (input.type === 'hidden' || input.type === 'file') return;
      var key = input.getAttribute('data-basari-field-key');
      var value = '';

      if (input.type === 'checkbox') {
        value = input.checked ? 'Yes' : 'No';
      } else if (key === 'phone') {
        value = [code, normalizeText(input.value)].filter(Boolean).join(' ');
      } else {
        value = normalizeText(input.value);
      }

      if (!value && key === 'member_id') value = 'Not provided';
      lines.push(controlLabel(input) + ': ' + value);
    });

    body.value = lines.join('\n');
  }

  function setSubmitting(form, isSubmitting) {
    var button = qs(form, SUBMIT_SELECTOR);
    var loading = qs(form, '[data-basari-insurance-submit-loading]');
    if (!button) return;

    if (isSubmitting) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (loading) loading.removeAttribute('hidden');
      return;
    }

    button.disabled = false;
    button.removeAttribute('aria-busy');
    if (loading) loading.setAttribute('hidden', '');
  }

  function getFormData(root) {
    var form = qs(root, FORM_SELECTOR);
    if (!form) return null;
    return collectFields(form);
  }

  function getFocusable(modal) {
    return qsa(modal, 'button, [href], input, select, textarea, [tabindex]').filter(function (el) {
      if (el.hasAttribute('disabled') || el.getAttribute('aria-hidden') === 'true') return false;
      if (el.tabIndex < 0) return false;
      return !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    });
  }

  function setModalState(root, open) {
    var modal = qs(root, MODAL_SELECTOR);
    if (!modal) return;

    if (open) {
      modal.removeAttribute('hidden');
      modal.setAttribute('aria-hidden', 'false');
      modal.setAttribute('data-basari-success-modal-state', 'open');
      emit(root, 'basari:insurance:modal:open', { modal: modal });
      var dialog = qs(modal, '[data-basari-success-modal-dialog]');
      if (dialog && typeof dialog.focus === 'function') dialog.focus();
      return;
    }

    modal.setAttribute('hidden', '');
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('data-basari-success-modal-state', 'closed');
    emit(root, 'basari:insurance:modal:close', { modal: modal });

    var submit = qs(root, SUBMIT_SELECTOR);
    if (submit && typeof submit.focus === 'function') submit.focus();
  }

  function openSuccessModal(root) {
    setModalState(root, true);
  }

  function closeSuccessModal(root) {
    setModalState(root, false);
  }

  function bindForm(root) {
    var form = qs(root, FORM_SELECTOR);
    if (!form || form.getAttribute('data-basari-insurance-form-bound') === 'true') return;
    form.setAttribute('data-basari-insurance-form-bound', 'true');

    form.addEventListener('input', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-basari-field-input')) return;
      clearFieldError(input);
    });

    form.addEventListener('change', function (event) {
      var input = event.target;
      if (!input || !input.hasAttribute || !input.hasAttribute('data-basari-field-input')) return;
      clearFieldError(input);
    });

    form.addEventListener('submit', function (event) {
      if (form.getAttribute('data-basari-insurance-submitting') === 'true') {
        event.preventDefault();
        return;
      }

      clearErrors(form);
      var invalid = validateForm(root, form);
      if (invalid.length) {
        event.preventDefault();
        invalid.forEach(function (item) {
          setFieldError(item.input, item.message);
        });
        if (typeof invalid[0].input.focus === 'function') invalid[0].input.focus();
        emit(root, 'basari:insurance:error', { form: form, errors: invalid });
        return;
      }

      writePayload(form);
      saveDraft(root, form);
      form.setAttribute('data-basari-insurance-submitting', 'true');
      setSubmitting(form, true);
      emit(root, 'basari:insurance:submit', {
        form: form,
        formData: collectFields(form)
      });

      global.setTimeout(function () {
        if (!form.isConnected) return;
        form.removeAttribute('data-basari-insurance-submitting');
        setSubmitting(form, false);
      }, SUBMIT_LOCK_MS);
    });

    global.addEventListener('pageshow', function () {
      form.removeAttribute('data-basari-insurance-submitting');
      setSubmitting(form, false);
    });
  }

  function bindModal(root) {
    var modal = qs(root, MODAL_SELECTOR);
    if (!modal || modal.getAttribute('data-basari-success-modal-bound') === 'true') return;
    modal.setAttribute('data-basari-success-modal-bound', 'true');

    qsa(modal, MODAL_CLOSE_SELECTOR).forEach(function (btn) {
      btn.addEventListener('click', function () {
        closeSuccessModal(root);
      });
    });

    var backdrop = qs(modal, MODAL_BACKDROP_SELECTOR);
    if (backdrop) {
      backdrop.addEventListener('click', function () {
        closeSuccessModal(root);
      });
    }

    document.addEventListener('keydown', function (event) {
      if (modal.getAttribute('data-basari-success-modal-state') !== 'open') return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeSuccessModal(root);
        return;
      }

      if (event.key !== 'Tab') return;
      var focusable = getFocusable(modal);
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      var active = document.activeElement;

      var activeIndex = focusable.indexOf(active);
      if (event.shiftKey && (active === first || activeIndex === -1)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || activeIndex === -1)) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function bindPostedState(root) {
    var form = qs(root, FORM_SELECTOR);
    var status = qs(root, '[data-basari-insurance-status]');
    if (!status) return;

    if (status.getAttribute('data-basari-insurance-status') === 'success') {
      clearDraft(root);
      openSuccessModal(root);
      emit(root, 'basari:insurance:success', { form: form });
      return;
    }

    if (status.getAttribute('data-basari-insurance-status') === 'error' && form) {
      restoreDraft(root, form);
      if (typeof status.focus === 'function') status.focus();
    }
  }

  function initRoot(root) {
    if (!root || root.getAttribute(READY_ATTR) === 'true') return;
    bindForm(root);
    bindModal(root);
    readStoredDraft(root);
    bindPostedState(root);
    root.setAttribute(READY_ATTR, 'true');
    emit(root, 'basari:insurance:ready', { root: root });
  }

  function init(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    qsa(context, ROOT_SELECTOR).forEach(initRoot);
  }

  BasariInsurance.init = init;
  BasariInsurance.getFormData = getFormData;
  BasariInsurance.openSuccessModal = openSuccessModal;
  BasariInsurance.closeSuccessModal = closeSuccessModal;
  BasariInsurance.adapters = BasariInsurance.adapters || {
    insuranceApi: null,
    email: null,
    crm: null,
    adminDashboard: null,
    customerNotifications: null,
    fileStorage: null,
    insuranceVerification: null
  };

  global.BasariInsurance = BasariInsurance;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})(typeof window !== 'undefined' ? window : this);
